/**
 * Broker Voice tone vocabulary shared by Venturu OS (web), the API, and jobs.
 *
 * The tone is stored as a plain string on `WorkspaceCommunicationProfile`.
 * Legacy values (`PRECISION`, `WARM`, `FORMAL`) are normalized on read so no
 * migration is required.
 */

export const BROKER_VOICE_TONES = [
  "CONVERSATIONAL",
  "FRIENDLY",
  "BALANCED",
  "DIRECT",
  "FORMAL",
] as const;

export type BrokerVoiceTone = (typeof BROKER_VOICE_TONES)[number];

export const DEFAULT_BROKER_VOICE_TONE: BrokerVoiceTone = "BALANCED";

export type BrokerVoiceToneMeta = {
  label: string;
  description: string;
  recommended: boolean;
};

export const BROKER_VOICE_TONE_META: Record<BrokerVoiceTone, BrokerVoiceToneMeta> =
  {
    CONVERSATIONAL: {
      label: "Conversational",
      description: "Sounds like a quick note from someone they already know.",
      recommended: false,
    },
    FRIENDLY: {
      label: "Friendly",
      description: "Warm and personal, with a clear next step.",
      recommended: false,
    },
    BALANCED: {
      label: "Balanced",
      description: "Approachable and professional. Works for most brokers.",
      recommended: true,
    },
    DIRECT: {
      label: "Direct",
      description: "Short, specific, and focused on what happens next.",
      recommended: false,
    },
    FORMAL: {
      label: "Formal",
      description: "Measured and polished, for a more traditional audience.",
      recommended: false,
    },
  };

const LEGACY_TONE_MAP: Record<string, BrokerVoiceTone> = {
  WARM: "FRIENDLY",
  PRECISION: "DIRECT",
  FORMAL: "FORMAL",
};

export function isBrokerVoiceTone(value: unknown): value is BrokerVoiceTone {
  return (
    typeof value === "string" &&
    (BROKER_VOICE_TONES as readonly string[]).includes(value)
  );
}

export function normalizeBrokerVoiceTone(
  value: string | null | undefined,
): BrokerVoiceTone {
  if (!value) return DEFAULT_BROKER_VOICE_TONE;
  const upper = value.trim().toUpperCase();
  if (isBrokerVoiceTone(upper)) return upper;
  return LEGACY_TONE_MAP[upper] ?? DEFAULT_BROKER_VOICE_TONE;
}

export function brokerVoiceToneIndex(tone: BrokerVoiceTone): number {
  return BROKER_VOICE_TONES.indexOf(tone);
}

export function brokerVoiceToneFromIndex(index: number): BrokerVoiceTone {
  const clamped = Math.min(
    BROKER_VOICE_TONES.length - 1,
    Math.max(0, Math.round(index)),
  );
  return BROKER_VOICE_TONES[clamped] ?? DEFAULT_BROKER_VOICE_TONE;
}

const TONE_BRIEFS: Record<BrokerVoiceTone, string> = {
  CONVERSATIONAL:
    "Conversational and relaxed. Write the way you would text a colleague you like: first names, contractions, short sentences, one idea per paragraph. Open with a natural line rather than a formal greeting. Close with a casual sign-off such as the broker's first name.",
  FRIENDLY:
    "Friendly and warm. Use first names and contractions, acknowledge the person's situation in one sentence, then get to the point. Keep paragraphs short. Close with a warm but brief sign-off.",
  BALANCED:
    "Approachable and professional. Plain language, no jargon, no filler. One sentence of context, the useful information, and a clear next step. Contractions are fine. Sign off with the broker's name.",
  DIRECT:
    "Direct and efficient. Lead with the point, keep sentences short, avoid pleasantries beyond a single greeting line, and end with exactly one concrete next step. Respect the reader's time.",
  FORMAL:
    "Formal and polished. Full sentences, no contractions, courteous and measured. Use a proper greeting and a traditional sign-off such as 'Kind regards'. Structure the message so it reads well to a traditional or institutional audience.",
};

/**
 * Turn a stored tone plus optional positioning into the single style brief
 * that every Venturu AI draft receives. Positioning is additive context and
 * never replaces the chosen tone.
 */
export function describeBrokerVoice(input: {
  tone: string | null | undefined;
  positioning?: string | null;
}): string {
  const tone = normalizeBrokerVoiceTone(input.tone);
  const brief = `Style (${BROKER_VOICE_TONE_META[tone].label}): ${TONE_BRIEFS[tone]}`;
  const positioning = input.positioning?.trim();
  if (!positioning) return brief;
  return `${brief}\nAbout the broker (shapes emphasis and tone only; never quote it, and never let it contradict or add to the verified facts): ${positioning}`;
}
